/**
 * Light cartographic map themes for the Uganda School Intelligence Platform.
 * Multiple light styles designed to feel like a high-end digital atlas.
 * NEVER use dark maps — all themes are bright, clean, and geographic.
 */

export type MapStyleId = "education-atlas" | "light-atlas" | "clean-street" | "terrain-light" | "satellite" | "hybrid-light";

// ── EDUCATION ATLAS — signature default style ────────────────────────────────
// Refined: warmer ivory base, richer water, graded roads, translucent POIs
const EDUCATION_ATLAS = [
  { elementType: "geometry", stylers: [{ color: "#f9f5ed" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2.5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#524a3e" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  // Water — soft, layered light blue
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#b8d8e8" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4a7a98" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2 }] },
  // Roads — clear hierarchy with warm tones
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f5d088" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#d4ae5e", weight: 1 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#8a6d28" }] },
  { featureType: "road.highway", elementType: "labels.text.stroke", stylers: [{ color: "#fff8e8" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#d8d4c8", weight: 0.8 }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#6a6558" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#ede8dc" }] },
  { featureType: "road.local", elementType: "geometry.stroke", stylers: [{ color: "#e0dcd0", weight: 0.4 }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#8a8478" }] },
  { featureType: "road.local", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  // Administrative boundaries — elegant restrained lines
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#c4b080", weight: 1.2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#7a6a40" }] },
  { featureType: "administrative", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#a89858", weight: 2.5 }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#d0c498", weight: 1.5 }] },
  { featureType: "administrative.locality", elementType: "geometry.stroke", stylers: [{ color: "#e4dcc4", weight: 0.8 }] },
  // Landscape — warm ivory land with subtle variation
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f9f5ed" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#f4eee0" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#eee8d4" }] },
  // Vegetation — subtle pale greens
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#e0e8d0" }] },
  { featureType: "landscape.natural.landcover", elementType: "labels.text.fill", stylers: [{ color: "#5a7a48" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#f0ece0" }] },
  // Parks / green spaces
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#d4e4c4" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#4a6a38" }] },
  // Urban areas — light neutral
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#ede8dc" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6a6558" }] },
  { featureType: "poi", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2 }] },
  // Schools — highlighted with warm tint
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#f0e4c8" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#5a4a20" }] },
  { featureType: "poi.school", elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  // Transit off
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  // Buildings — subtle 3D feel
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#f0ece0" }] },
  { featureType: "building", elementType: "geometry.fill", stylers: [{ color: "#ece6d8" }] },
  // Land parcel lines — very subtle
  { featureType: "landscape.man_made", elementType: "geometry.stroke", stylers: [{ color: "#e8e2d4", weight: 0.5 }] },
];

// ── LIGHT ATLAS — warm cartographic with refined roads ────────────────────────
const LIGHT_ATLAS = [
  { elementType: "geometry", stylers: [{ color: "#f7f2e8" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2.5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#524a3e" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c0dcec" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4a7a98" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f0d098" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#d4ae5e", weight: 0.8 }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#d8d4c8", weight: 0.6 }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#ede8dc" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#8a8478" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#c4b080", weight: 1.2 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#a89858", weight: 2 }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#d0c498", weight: 1.2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#7a6a40" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f7f2e8" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#f0ebe0" }] },
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#e0e8d0" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#d4e4c4" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#4a6a38" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#f0e4c8" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#5a4a20" }] },
  { featureType: "poi", stylers: [{ visibility: "simplified" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#f0ece0" }] },
];

// ── CLEAN STREET — minimal modern street map ─────────────────────────────────
const CLEAN_STREET = [
  { elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#7a7a7a" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#cee8f4" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#5a9ab8" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ffe0a0" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#e0c070", weight: 0.8 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#9a7a30" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#e8e8e8", weight: 0.5 }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#888888" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#f0f0f0" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#aaaaaa" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#d0d0d0", weight: 1 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#b0b0b0", weight: 2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#999999" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#fafafa" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#f5f5f0" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.school", stylers: [{ visibility: "on" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#f8f0d8" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#5a4a20" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e8f0e0" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
];

// ── TERRAIN LIGHT — bright topographic with elevation hints ───────────────────
const TERRAIN_LIGHT = [
  { elementType: "geometry", stylers: [{ color: "#f4eee0" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 2.5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#5a5246" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#b8d4e4" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4a7a98" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f0d898" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#d4ae5e", weight: 0.8 }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry.stroke", stylers: [{ color: "#d8d4c8", weight: 0.6 }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#eae4d4" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#8a8478" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#c4b080", weight: 1.2 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#a89858", weight: 2 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#7a6a40" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f4eee0" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#ece4d0" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#e8dcc0" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry.fill", stylers: [{ color: "#e4d8bc" }] },
  { featureType: "landscape.natural.landcover", elementType: "geometry", stylers: [{ color: "#d8e0c8" }] },
  { featureType: "landscape.natural.landcover", elementType: "labels.text.fill", stylers: [{ color: "#4a6a38" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#d0dcb8" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#4a6a38" }] },
  { featureType: "poi.school", elementType: "geometry", stylers: [{ color: "#f0e4c8" }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#5a4a20" }] },
  { featureType: "poi", stylers: [{ visibility: "simplified" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "building", elementType: "geometry", stylers: [{ color: "#ece6d4" }] },
];

// ── HYBRID LIGHT — satellite with clean labels ───────────────────────────────
const HYBRID_LIGHT = [
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3.5 }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#2a2a2a" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ visibility: "simplified" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 1 }] },
  { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#ffe0a0", weight: 2 }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 1 }] },
  { featureType: "road.arterial", elementType: "geometry.fill", stylers: [{ color: "#ffffff", weight: 1 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 1.5 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#ffffff", weight: 2.5 }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "administrative", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 4 }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#2060a0" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
  { featureType: "poi.school", elementType: "labels.text.fill", stylers: [{ color: "#3a2a10" }] },
  { featureType: "poi.school", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
  { featureType: "landscape", elementType: "labels.text.fill", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "landscape", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff", weight: 3 }] },
];

export const MAP_STYLES: Record<MapStyleId, { label: string; description: string; styles: any[]; mapTypeId?: string }> = {
  "education-atlas": { label: "Education Atlas", description: "Signature platform map with warm tones and school focus", styles: EDUCATION_ATLAS },
  "light-atlas": { label: "Light Atlas", description: "Elegant cartographic map with warm land tones", styles: LIGHT_ATLAS },
  "clean-street": { label: "Clean Street", description: "Minimal modern street map", styles: CLEAN_STREET },
  "terrain-light": { label: "Terrain Light", description: "Bright topographic with elevation hints", styles: TERRAIN_LIGHT },
  "satellite": { label: "Satellite", description: "Natural high-resolution imagery", styles: [], mapTypeId: "satellite" },
  "hybrid-light": { label: "Hybrid Light", description: "Satellite with clean roads and labels", styles: HYBRID_LIGHT, mapTypeId: "hybrid" },
};

// Default map options — Education Atlas, centered on Uganda
export const LIGHT_MAP_OPTIONS = {
  center: { lat: 1.3733, lng: 32.2903 },
  zoom: 7,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  styles: EDUCATION_ATLAS,
  backgroundColor: "#f9f5ed",
  gestureHandling: "greedy" as const,
  mapTypeId: "roadmap",
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: { position: 8 }, // RIGHT_BOTTOM
  minZoom: 6,
  maxZoom: 20,
};

// School type icon colors for markers — all light/clean
export const SCHOOL_TYPE_COLORS: Record<string, string> = {
  primary: "#2e8b4a",
  secondary: "#2a6ab0",
  nursery: "#d07a30",
  vocational: "#7a4a8a",
  tertiary: "#b04040",
  "special needs": "#4a8a8a",
};

export const OWNERSHIP_RING_COLORS: Record<string, string> = {
  government: "#2a6ab0",
  private: "#2e8b4a",
  community: "#d07a30",
  "faith-based": "#7a4a8a",
  ngo: "#b04040",
};
